import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, CheckCircle2, XCircle, AlertCircle, FileText, Send } from 'lucide-react';
import { toast } from 'sonner';

const todayISO = () => new Date().toISOString().slice(0, 10);

// Public-only axios instance (no Authorization interceptor)
const publicApi = axios.create({ baseURL: '/api' });

export default function PublicTestPage() {
  const { token } = useParams();
  const [phase, setPhase] = useState('intro'); // intro | running | result | invalid
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  // candidate
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [fiscalCode, setFiscalCode] = useState('');
  const [testDate, setTestDate] = useState(todayISO());
  // answers
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [openedAt, setOpenedAt] = useState(null); // v1.3.21: istante di apertura del test

  useEffect(() => {
    (async () => {
      try {
        const r = await publicApi.get(`/public/training-tests/${token}`);
        setTest(r.data);
        setAnswers(Array(r.data.questions.length).fill(-1));
      } catch (e) {
        setPhase('invalid');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const start = () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Inserisci Nome e Cognome');
      return;
    }
    if (!email.includes('@')) {
      toast.error('Inserisci una email valida');
      return;
    }
    setOpenedAt(new Date().toISOString());
    setPhase('running');
  };

  const setAnswer = (qIdx, optIdx, isMulti) => {
    setAnswers((prev) => {
      const next = [...prev];
      if (isMulti) {
        const current = Array.isArray(next[qIdx]) ? [...next[qIdx]] : [];
        const i = current.indexOf(optIdx);
        if (i === -1) current.push(optIdx); else current.splice(i, 1);
        next[qIdx] = current.sort((a,b) => a - b);
      } else {
        next[qIdx] = optIdx;
      }
      return next;
    });
  };

  const isAnswered = (a) => {
    if (Array.isArray(a)) return a.length > 0;
    return a !== -1 && a !== undefined;
  };

  const submit = async () => {
    const unanswered = answers.filter((a) => !isAnswered(a)).length;
    if (unanswered > 0) {
      if (!window.confirm(`Hai ${unanswered} domande senza risposta. Inviare comunque?`)) return;
    }
    setSubmitting(true);
    try {
      const r = await publicApi.post(`/public/training-tests/${token}/submit`, {
        candidate_first_name: firstName,
        candidate_last_name: lastName,
        candidate_email: email,
        candidate_fiscal_code: fiscalCode || undefined,
        test_date: testDate,
        opened_at: openedAt || new Date().toISOString(),
        answers,
      });
      setResult(r.data);
      setPhase('result');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio test');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-primary" /></div>;
  }

  if (phase === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 to-slate-200">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold">Link non valido</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Questo link al test è scaduto o è stato revocato. Contatta il tuo formatore per riceverne uno nuovo.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-200 p-4 lg:p-8">
      <div className="max-w-3xl mx-auto" data-testid="public-test-page">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold mb-3">
            <FileText className="w-3 h-3" /> CEN ENGINEERING · TEST DI FORMAZIONE
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold">{test?.title}</h1>
          {test?.duration_minutes ? (
            <p className="text-sm text-muted-foreground mt-1">Durata indicativa: {test.duration_minutes} min · Soglia: {test.passing_score_pct || 70}%</p>
          ) : null}
        </div>

        {/* INTRO — form anagrafica */}
        {phase === 'intro' && (
          <Card data-testid="public-test-intro">
            <CardHeader>
              <CardTitle>I tuoi dati</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Alert>
                <AlertDescription className="text-xs">
                  I dati che inserisci servono a generare il tuo attestato di partecipazione. Verifica accuratamente Nome, Cognome e Codice Fiscale.
                </AlertDescription>
              </Alert>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nome *</Label>
                  <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Marco" data-testid="first-name-input" />
                </div>
                <div>
                  <Label>Cognome *</Label>
                  <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Rossi" data-testid="last-name-input" />
                </div>
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="marco.rossi@email.it" data-testid="email-input" />
              </div>
              <div>
                <Label>Codice Fiscale</Label>
                <Input value={fiscalCode} onChange={(e) => setFiscalCode(e.target.value.toUpperCase())} placeholder="RSSMRC80A01H501Z" maxLength={16} data-testid="fiscal-code-input" />
              </div>
              <div>
                <Label>Data di somministrazione *</Label>
                <Input type="date" value={testDate} onChange={(e) => setTestDate(e.target.value)} data-testid="test-date-input" />
              </div>
              <Button className="w-full" onClick={start} data-testid="start-test-btn">
                Inizia il Test ({test?.question_count} domande)
              </Button>
            </CardContent>
          </Card>
        )}

        {/* RUNNING — domande */}
        {phase === 'running' && (
          <div className="space-y-4" data-testid="public-test-running">
            <Card>
              <CardContent className="p-3 flex items-center justify-between text-sm">
                <div><strong>Candidato:</strong> {firstName} {lastName}</div>
                <div className="text-muted-foreground">{answers.filter(isAnswered).length} / {test.questions.length} risposte</div>
              </CardContent>
            </Card>
            {test.questions.map((q, qi) => {
              const isMulti = !!q.is_multi;
              const ans = answers[qi];
              const isSelected = (oi) => isMulti ? (Array.isArray(ans) && ans.includes(oi)) : ans === oi;
              return (
                <Card key={qi} data-testid={`question-${qi}`}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-semibold bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Q{qi + 1}</span>
                      <h3 className="font-medium">{q.q}</h3>
                    </div>
                    {isMulti && (
                      <p className="text-[11px] font-semibold text-orange-700 bg-orange-50 px-2 py-1 rounded inline-block">
                        ⚠️ Risposta multipla: seleziona TUTTE le opzioni corrette
                      </p>
                    )}
                    <div className="space-y-1.5 pl-2">
                      {(q.options || []).map((opt, oi) => (
                        <label key={oi} className={`flex items-start gap-2 p-2 rounded cursor-pointer border transition ${isSelected(oi) ? 'border-blue-500 bg-blue-50' : 'border-transparent hover:bg-muted/50'}`}>
                          <input
                            type={isMulti ? 'checkbox' : 'radio'}
                            name={`q-${qi}`}
                            checked={isSelected(oi)}
                            onChange={() => setAnswer(qi, oi, isMulti)}
                            data-testid={`q${qi}-opt${oi}`}
                          />
                          <span className="text-sm">{opt}</span>
                        </label>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            <div className="sticky bottom-3">
              <Button className="w-full text-base py-6" onClick={submit} disabled={submitting} data-testid="submit-test-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Invia risposte
              </Button>
            </div>
          </div>
        )}

        {/* RESULT — esito */}
        {phase === 'result' && result && (
          <div className="space-y-4" data-testid="public-test-result">
            <Card className={result.passed ? 'border-emerald-300' : 'border-red-300'}>
              <CardContent className="p-6 text-center">
                {result.passed ? (
                  <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-3" />
                ) : (
                  <XCircle className="w-16 h-16 text-red-500 mx-auto mb-3" />
                )}
                <h2 className={`text-3xl font-bold ${result.passed ? 'text-emerald-700' : 'text-red-700'}`} data-testid="result-status">
                  {result.passed ? 'Test superato!' : 'Test non superato'}
                </h2>
                <p className="text-sm text-muted-foreground mt-2">
                  Candidato: <strong>{result.candidate_name}</strong>
                </p>
                <div className="grid grid-cols-3 gap-4 mt-6">
                  <div>
                    <div className="text-3xl font-bold" data-testid="result-score">{result.score_pct}%</div>
                    <div className="text-xs text-muted-foreground">Punteggio</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-emerald-600">{result.correct_count}</div>
                    <div className="text-xs text-muted-foreground">Risposte corrette</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-red-600">{result.total_questions - result.correct_count}</div>
                    <div className="text-xs text-muted-foreground">Risposte sbagliate</div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  Soglia minima per superare: {result.passing_score_pct}%
                </p>
              </CardContent>
            </Card>

            {/* Dettaglio risposte */}
            <h3 className="text-lg font-bold pt-2">Dettaglio risposte</h3>
            {result.detail.map((d, di) => (
              <Card key={di} className={d.is_correct ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-red-500'}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start gap-2">
                    {d.is_correct
                      ? <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      : <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />}
                    <h4 className="font-medium text-sm">{d.q_index + 1}. {d.question}</h4>
                  </div>
                  <div className="pl-7 space-y-1">
                    {d.options.map((opt, oi) => {
                      const isUserPick = Array.isArray(d.selected) ? d.selected.includes(oi) : oi === d.selected;
                      const isCorrect = Array.isArray(d.correct) ? d.correct.includes(oi) : oi === d.correct;
                      let cls = 'p-2 rounded text-xs flex items-center gap-2 ';
                      if (isCorrect) cls += 'bg-emerald-100 text-emerald-900 font-semibold ';
                      else if (isUserPick) cls += 'bg-red-100 text-red-900 ';
                      else cls += 'text-muted-foreground ';
                      return (
                        <div key={oi} className={cls}>
                          {isCorrect && <CheckCircle2 className="w-3 h-3" />}
                          {!isCorrect && isUserPick && <XCircle className="w-3 h-3" />}
                          <span>{opt}</span>
                          {isUserPick && <span className="ml-auto text-[10px] bg-white px-1.5 py-0.5 rounded border">tua risposta</span>}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}

            <div className="text-center pt-4 pb-8">
              <p className="text-xs text-muted-foreground">
                Risultato salvato — Sarà inviato al tuo formatore. Puoi chiudere questa pagina.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
